import { motion } from "framer-motion";
import { Twitter, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";

const supportChannels = [
  {
    icon: <Twitter className="w-6 h-6" />,
    title: "Twitter Support",
    description: "Follow us on Twitter for updates and support announcements.",
    link: "https://x.com/cryptfluencers",
    buttonText: "Follow Us",
    isExternal: true
  },
  {
    icon: <FileText className="w-6 h-6" />,
    title: "Documentation",
    description: "Check our comprehensive documentation for technical details.",
    link: "/documentation",
    buttonText: "View Docs",
    isExternal: false
  }
];

const commonIssues = [
  {
    title: "Wallet Connection",
    steps: [
      "Ensure your wallet is installed and up to date",
      "Connect to the correct network (Ethereum, Base, PulseChain, or Chia)",
      "Clear browser cache and try reconnecting",
      "Check if your wallet supports the selected network"
    ]
  },
  {
    title: "Minting Issues",
    steps: [
      "Verify you have sufficient funds for gas fees",
      "Confirm the network is not congested",
      "Check if the maximum mint limit has been reached",
      "Ensure you're not exceeding the per-transaction limit"
    ]
  },
  {
    title: "NFT Visibility",
    steps: [
      "Wait for blockchain confirmation (varies by network)",
      "Add the NFT contract address to your wallet",
      "Refresh your wallet metadata",
      "Check the blockchain explorer for transaction status"
    ]
  }
];

export default function Support() {
  return (
    <Layout>
      <div className="prose prose-invert max-w-4xl mx-auto">
        <h1 className="text-2xl font-pixel mb-8 text-[#9333EA]">Support</h1>
        <div className="space-y-8 text-xs leading-relaxed text-gray-400">
          <section>
            <h2 className="text-base font-pixel mb-4 text-[#9333EA]">Need Help?</h2>
            <p className="mb-4">We're here to assist you with any questions or issues you might have. Choose your preferred support channel below:</p>
          </section>

          <section>
            <h2 className="text-base font-pixel mb-4 text-[#9333EA]">Support Channels</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {supportChannels.map((channel) => (
                <div key={channel.title} className="bg-black/60 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <div className="flex items-center gap-3 mb-3">
                    {channel.icon}
                    <h3 className="text-base font-pixel text-white">{channel.title}</h3>
                  </div>
                  <p className="mb-4">{channel.description}</p>
                  {channel.isExternal ? (
                    <a
                      href={channel.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#9333EA] hover:text-[#9333EA]/80 transition-colors"
                    >
                      {channel.buttonText} →
                    </a>
                  ) : (
                    <Link
                      to={channel.link}
                      className="inline-flex items-center text-[#9333EA] hover:text-[#9333EA]/80 transition-colors"
                    >
                      {channel.buttonText} →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-base font-pixel mb-4 text-[#9333EA]">Common Issues</h2>
            <div className="space-y-6">
              {commonIssues.map((issue) => (
                <div key={issue.title} className="bg-black/60 backdrop-blur-sm rounded-lg p-6 border border-white/10">
                  <h3 className="text-base font-pixel mb-4 text-white">{issue.title}</h3>
                  <ul className="space-y-2 list-disc list-inside">
                    {issue.steps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
} 